import { cn } from 'cn'
import { CalendarDays } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { formatDisplayDate } from '@/lib/utils'
import { BottomSheet } from '@/ui/bottom-sheet'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui/checkbox'
import { DatePicker } from '@/ui/date-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'

/**
 * Shared building blocks for the history filter sheets.
 *
 * `GrFilterSheet` and `GiFilterSheet` were 127 identical lines apart — the same
 * status checkboxes, the same select, the same date button, the same actions and
 * the same date-picker wiring, differing only in labels and one extra section.
 * That duplication was large enough on its own to fail the gate's new-code
 * duplication condition, and it meant every fix to one sheet had to be
 * remembered for the other.
 *
 * Markup and class names are carried over verbatim, so the rendered output is
 * unchanged.
 */

export const ALL = '__all__'

export function isoToDate(iso: string): Date | null {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y as number, (m as number) - 1, d as number)
}

export function dateToIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * Map a select value back to the stored filter.
 *
 * The sentinel must never reach the filter comparison — it is a Radix
 * implementation detail, and leaking it would silently match nothing. Named and
 * exported so it can be tested directly: driving a real Radix listbox costs ~90s
 * per test under CI, which is not a price worth paying to check a ternary.
 */
export function filterValueFrom(selected: string): string {
  return selected === ALL ? '' : selected
}

/** A titled section with the divider every section in these sheets carries. */
export function Section({
  title,
  dataTour,
  children,
}: Readonly<{ title?: string; dataTour?: string; children: ReactNode }>) {
  return (
    <div className="flex flex-col gap-4" data-tour={dataTour}>
      {title && <p className="text-base font-bold text-foreground">{title}</p>}
      {children}
      <hr className="border-border" />
    </div>
  )
}

export interface FilterOption<T extends string> {
  value: T
  label: string
}

/**
 * A checkbox list. `idPrefix` keeps the `htmlFor`/`id` pairs unique when two
 * sheets are mounted at once — which is exactly what HistoryScreen does.
 */
export function CheckboxFilterSection<T extends string>({
  title,
  idPrefix,
  options,
  selected,
  onToggle,
  dataTour,
  optionDataTourPrefix,
}: Readonly<{
  title: string
  idPrefix: string
  options: readonly FilterOption<T>[]
  selected: readonly T[]
  onToggle: (value: T) => void
  dataTour?: string
  /** When set, each row gets `data-tour={`${prefix}${value}`}` — lets a tour
   *  spotlight a specific option (e.g. "Completed") rather than the whole group. */
  optionDataTourPrefix?: string
}>) {
  return (
    <Section title={title} dataTour={dataTour}>
      <div className="flex flex-col gap-5">
        {options.map(({ value, label }) => (
          <div
            key={value}
            className="flex items-center gap-3"
            data-tour={optionDataTourPrefix ? `${optionDataTourPrefix}${value}` : undefined}
          >
            <Checkbox
              id={`${idPrefix}-${value}`}
              checked={selected.includes(value)}
              onCheckedChange={() => onToggle(value)}
            />
            <label
              htmlFor={`${idPrefix}-${value}`}
              className="cursor-pointer text-base font-medium text-foreground"
            >
              {label}
            </label>
          </div>
        ))}
      </div>
    </Section>
  )
}

/** A single-select section. Empty string means "all", which the sheet stores. */
export function SelectFilterSection({
  title,
  placeholder,
  allLabel,
  value,
  options,
  onChange,
}: Readonly<{
  title: string
  placeholder: string
  allLabel: string
  value: string
  options: readonly string[]
  onChange: (value: string) => void
}>) {
  return (
    <Section title={title}>
      <Select value={value || ALL} onValueChange={(v) => onChange(filterValueFrom(v))}>
        <SelectTrigger className="h-10 rounded-full px-6">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Section>
  )
}

export function DateFilterSection({
  value,
  onOpen,
  title = 'Date',
}: Readonly<{ value: Date | null; onOpen: () => void; title?: string }>) {
  return (
    <Section title={title}>
      <button
        type="button"
        onClick={onOpen}
        className="flex h-10 w-full items-center justify-between rounded-full border border-border px-6"
      >
        <span
          className={cn('text-sm font-medium', value ? 'text-foreground' : 'text-muted-foreground')}
        >
          {value ? formatDisplayDate(value) : 'Select date'}
        </span>
        <CalendarDays className="size-5 text-muted-foreground" />
      </button>
    </Section>
  )
}

export function FilterActions({
  onApply,
  onClear,
  applyDataTour,
  clearDisabled,
}: Readonly<{
  onApply: () => void
  onClear: () => void
  applyDataTour?: string
  clearDisabled?: boolean
}>) {
  return (
    <div className="flex items-center justify-center gap-4">
      <Button
        variant="outline"
        className="h-(--spacing-touch) w-full rounded-full border-foreground flex-1"
        onClick={onClear}
        disabled={clearDisabled}
      >
        Clear all
      </Button>
      <Button
        className="h-(--spacing-touch) w-full rounded-full flex-1"
        onClick={onApply}
        data-tour={applyDataTour}
      >
        Apply filters
      </Button>
    </div>
  )
}

/**
 * The sheet shell: everything both filter sheets share, with the sections that
 * differ passed in. Owns the date-picker state, because both sheets did.
 */
export function FilterSheetShell({
  open,
  onClose,
  filterDate,
  onDateChange,
  onApply,
  onClear,
  applyDataTour,
  title = 'Sort & Filter',
  dateTitle,
  children,
}: Readonly<{
  open: boolean
  onClose: () => void
  /** ISO date string, '' for unset. */
  filterDate: string
  onDateChange: (iso: string) => void
  onApply: () => void
  onClear: () => void
  /** `data-tour` anchor forwarded onto the Apply filters button. */
  applyDataTour?: string
  /** Sheet header title. Defaults to "Sort & Filter". */
  title?: string
  /** Heading over the date field — production calls it "Creation date" on GI. */
  dateTitle?: string
  /** The sections unique to this sheet, rendered above the date section. */
  children: ReactNode
}>) {
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const dateValue = isoToDate(filterDate)

  return (
    <>
      <BottomSheet open={open} onClose={onClose} title={title} scrollable={false}>
        {/* `scrollable={false}` on the sheet gives this layout exclusive control
         * of the scroll area: sections scroll inside the `flex-1` region while
         * the actions stay pinned at the bottom via `shrink-0`. */}
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-4">
          {children}
          <div data-tour="adj-filter-date">
            <DateFilterSection
              value={dateValue}
              onOpen={() => setDatePickerOpen(true)}
              title={dateTitle}
            />
          </div>
        </div>
        <div className="shrink-0 bg-background pt-4">
          <FilterActions onApply={onApply} onClear={onClear} applyDataTour={applyDataTour} />
        </div>
      </BottomSheet>

      <DatePicker
        open={datePickerOpen}
        value={dateValue}
        onSelect={(date) => {
          onDateChange(dateToIso(date))
          setDatePickerOpen(false)
        }}
        onClose={() => setDatePickerOpen(false)}
      />
    </>
  )
}
